import { IShipment } from 'types'

export const appInstructions = [
  {
    label: 'Request app download',
    value:
      'Download the free Transfix app to view load info, track, and get fewer calls from us. https://apps.transfix.io/download',
  },
  {
    label: 'Complete app setup',
    value: `Please login to the Transfix app and enable gps tracking for this load. To login, first enter your cell number, then enter the SMS verification code you received. To turn on tracking select "Allow while using" and "Always Allow" on the location sharing prompts, or tap the red banner on 'My Loads' screen to go to the device settings. Please ensure that your location permissions are set to “Always Share”`,
  },
  {
    label: 'Must track warning',
    value:
      'Please be advised that GPS tracking is required for this load. You are at risk of being removed from the shipment if you do not comply. Download the free Transfix app while stopped. https://apps.transfix.io/download',
  },
  {
    label: 'Nudge for location ping',
    value:
      'While stopped, please go to Profile tab in the Transfix mobile app and select "Share current location" to send a one time location ping.',
  },
]

export const appInstructionsSpanish = [
  {
    label: 'Request app download (Spanish)',
    value:
      'Baja nuestra applicación mobile de Transfix para ver información de la carga y para el seguimiento/tracking: https://apps.transfix.io/download',
  },
  {
    label: 'Complete app setup (Spanish)',
    value:
      'Inicie sesión en la aplicación Transfix y habilite el rastreo gps para esta carga. Para iniciar sesión, ingrese su número de celular y luego el código de verificación de SMS. Para activar el seguimiento, seleccione "Permitir mientras se usa" y "Permitir siempre" en las indicaciones para compartir la ubicación o toque el boton rojo en la pantalla de "Mis cargas". Por ultimo vaya a la configuración del dispositivo y asegúrese de que los permisos de ubicación estén configurados en "Siempre".',
  },
  {
    label: 'Must track warning (Spanish)',
    value:
      'Tenga en cuenta que se requiere seguimiento por GPS para esta carga. Corre el riesgo de ser retirado de la carga si no cumple. Descargue la aplicación de Transfix gratuita mientras está detenido. https://apps.transfix.io/download',
  },
  {
    label: 'Nudge for location ping (Spanish)',
    value:
      'Mientras está detenido, reactive el seguimiento ingresando a la aplicación y haciendo clic en Configuración en la parte inferior derecha de la pantalla y seleccionando Compartir ubicación actual.',
  },
]

export const appSetup = [
  {
    label: 'App Setup/Use Instructions',
    value:
      'How to set up and use your Transfix app: https://support.transfix.io/en/articles/6236404-fms-transfix-mobile-app-how-to-set-up-tracking-via-the-app-and-self-update',
  },
]

export const appSetupSpanish = [
  {
    label: 'App Setup/Use Instructions (Spanish)',
    value:
      'Cómo usar su aplicación Transfix: https://support.transfix.io/en/collections/3616096-transfix-mobile-app',
  },
]

export const confirmOut = [
  {
    label: 'PICKUP - confirm out',
    value: 'Are you loaded? Please let us know your check out time.',
  },
  {
    label: 'DELIVERY - confirm out',
    value: 'Are you empty? Please let us know your check out time.',
  },
]

export const confirmOutSpanish = [
  {
    label: 'PICKUP - confirm out (Spanish)',
    value: '¿Estás cargado? Por favor, informanos de tu hora de salida.',
  },
  {
    label: 'DELIVERY - confirm out (Spanish)',
    value: '¿Estás vacio? Por favor, informanos de tu hora de salida.',
  },
]

export const survey = [
  {
    label: 'POD request',
    value:
      'Thanks for working with us! For faster payment, please respond with a picture of your signed BOL ASAP.',
  },
]

export const surveySpanish = [
  {
    label: 'POD request (Spanish)',
    value:
      '¡Gracias por trabajar con nosotros! Para un pago más rápido, responda con una imagen de su BOL firmado lo antes posible.',
  },
]

export const CANNED_MESSAGES = [
  ...appInstructions,
  ...confirmOut,
  ...survey,
  ...appSetup,
  ...appInstructionsSpanish,
  ...confirmOutSpanish,
  ...surveySpanish,
  ...appSetupSpanish,
]

type TBaseIShipmentStop = IShipment['stops'][number]
type TBaseIShipmentFacility = IShipment['stops'][number]['facility']

interface IPresentMessageFacility extends Pick<TBaseIShipmentFacility, 'city'> {
  detail: {
    location: {
      stateCode: TBaseIShipmentFacility['detail']['location']['stateCode']
    }
  }
}

interface IPresentMessageStop extends Pick<TBaseIShipmentStop, 'type'> {
  facility: IPresentMessageFacility
}
export interface IPresentMessageShipment {
  status: Pick<IShipment['status'], 'currentStopIndex'>
  stops: IPresentMessageStop[]
}

export const PRESET_MESSAGES = (shipment: IPresentMessageShipment) => {
  const { status: { currentStopIndex } = {}, stops = [] } = shipment
  let pickupCity
  let deliveryCity
  let pickupState
  let deliveryState

  if (Number.isInteger(currentStopIndex) && currentStopIndex !== undefined) {
    for (let i = 0; i < currentStopIndex; i += 1) {
      const currentStop = stops[i]
      const {
        facility: { city, detail: { location: { stateCode: state } = {} } = {} } = {},
        type,
      } = currentStop
      if (type === 'pickup') {
        pickupCity = city
        pickupState = state
      }
      if (type === 'delivery') {
        deliveryCity = city
        deliveryState = state
      }
      if (pickupCity && deliveryCity) break
    }
    for (let i = currentStopIndex; i < stops.length; i += 1) {
      const currentStop = stops[i]
      const {
        facility: { city, detail: { location: { stateCode: state } = {} } = {} } = {},
        type,
      } = currentStop

      // if we are pre-transit, pickup city should be set as the first stop
      if (type === 'pickup' && currentStopIndex === 0 && i === 0) {
        pickupCity = city
        pickupState = state
      }
      if (type === 'pickup' && currentStopIndex !== 0) {
        pickupCity = city
        pickupState = state
      }
      if (type === 'delivery') {
        deliveryCity = city
        deliveryState = state
      }
      if (pickupCity && deliveryCity) break
    }
  }

  const confirmation = [
    {
      label: 'Confirm MT/ETA',
      value: `Are you empty and ready for pickup in ${pickupCity}, ${pickupState}? Please reply when stopped.`,
    },
    {
      label: 'Confirm delivery ETA',
      value: `Are you on your way to the delivery in ${deliveryCity}, ${deliveryState}? Please let us know your ETA when stopped.`,
    },
    {
      label: 'PICKUP - confirm in',
      value: `Have you checked in at ${pickupCity}, ${pickupState}? If so, what time? Please respond when you are stopped.`,
    },
    {
      label: 'DELIVERY - confirm in',
      value: `Have you checked in at ${deliveryCity}, ${deliveryState} If so, what time? Please respond when you are stopped.`,
    },
  ]

  const confirmationSpanish = [
    {
      label: 'Confirm MT/ETA (Spanish)',
      value: `¿Estas vacio y listo para ser el recogida en ${pickupCity}, ${pickupState}? Responda cuando se estacionado.`,
    },
    {
      label: 'Confirm delivery ETA (Spanish)',
      value: `¿Estas en camino a la entrega en ${deliveryCity}, ${deliveryState}? Por favor, dejanos saber el tiempo que vas a llegar cuando estas estacionado.`,
    },
    {
      label: 'PICKUP - confirm in (Spanish)',
      value: `¿Te has registrado en la ${pickupCity}, ${pickupState}? Responda con el tiempo que llegastes, cuando estas estacionado.`,
    },
    {
      label: 'DELIVERY - confirm in (Spanish)',
      value: `¿Te has registrado en la ${deliveryCity}, ${deliveryState}? Responda con el tiempo que llegastes, cuando estas estacionado.`,
    },
  ]

  return [
    ...appInstructions,
    ...confirmation,
    ...confirmOut,
    ...survey,
    ...appSetup,
    ...appInstructionsSpanish,
    ...confirmationSpanish,
    ...confirmOutSpanish,
    ...surveySpanish,
    ...appSetupSpanish,
  ]
}

export const INVALID_NUMBER_ERROR_MESSAGES = [
  'is not a valid phone number',
  'is not a mobile number',
  'is not currently reachable using',
  'Account must be active to send SMS',
]

export const TWILIO_ERROR_MESSAGES = [
  'Twilio::REST::ServerError',
  'execution expired',
  'Authenticate',
  'Not sent to bot user',
]

export const OPT_OUT_MESSAGES = ['violates a blacklist rule']
